import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSettings } from '../App';
import confetti from 'canvas-confetti';
import EtapePlateaux from '../components/EtapePlateaux';
import EtapeExtras from '../components/EtapeExtras';
import EtapeSlot from '../components/EtapeSlot';
import EtapeCoordonnees from '../components/EtapeCoordonnees';
import EtapeConfirmation from '../components/EtapeConfirmation';
import Icon from '../components/Icon';
import './Precommande.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function fmt$(v) {
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

// ─── Étapes : 1-Plateaux | 2-Extras | 3-Créneau | 4-Coordonnées | (5-Paiement via EtapeConfirmation)
const ETAPES = [
  { num: 1, label: 'Plateaux' },
  { num: 2, label: 'Extras' },
  { num: 3, label: 'Créneau' },
  { num: 4, label: 'Coordonnées' },
];

// ─── Stepper premium ──────────────────────────
function Stepper({ etape }) {
  return (
    <div className="stepper-wrap">
      <div className="stepper">
        {ETAPES.map((e, i) => (
          <div key={e.num} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <div className={`step ${etape === e.num ? 'actif' : etape > e.num ? 'fait' : ''}`}>
              <div className="step-num">
                {etape > e.num
                  ? <Icon name="check" size={14} weight="bold" color="currentColor" />
                  : e.num
                }
              </div>
              <div className="step-label">{e.label}</div>
            </div>
            {i < ETAPES.length - 1 && <div className="step-line" />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Panier flottant premium ──────────────────
function PanierFlottant({ evenement, selection, selectionExtras, slotSelectionne }) {
  const plateaux = evenement?.plateaux || [];
  const extras   = evenement?.extras   || [];

  const itemsPlateaux = plateaux.filter(p => (selection[p.id] || 0) > 0);
  const itemsExtras   = extras.filter(e => (selectionExtras[e.id] || 0) > 0);

  const totalPlateaux = itemsPlateaux.reduce((acc, p) => acc + (selection[p.id] || 0) * p.prix, 0);
  const totalExtras   = itemsExtras.reduce((acc, e) => acc + (selectionExtras[e.id] || 0) * e.prix, 0);
  const total         = totalPlateaux + totalExtras;

  const nbPersonnes = itemsPlateaux.reduce(
    (acc, p) => acc + (selection[p.id] || 0) * (p.taille || p.nb_personnes || 1), 0
  );

  const nbItems = itemsPlateaux.length + itemsExtras.length;

  return (
    <div className="panier-card">
      <div className="panier-header">
        <span className="panier-titre">Votre commande</span>
        <span className="panier-nb-items">{nbItems}</span>
      </div>
      <div className="panier-body">
        {nbItems === 0 ? (
          <div className="panier-empty">Aucun article sélectionné</div>
        ) : (
          <>
            {itemsPlateaux.map(p => (
              <div key={p.id} className="panier-item">
                <div>
                  <div className="panier-item-nom">
                    {selection[p.id]}&times; {p.label}
                  </div>
                </div>
                <div className="panier-item-prix">{fmt$(p.prix * selection[p.id])}</div>
              </div>
            ))}
            {itemsExtras.map(e => (
              <div key={e.id} className="panier-item">
                <div className="panier-item-nom">
                  {selectionExtras[e.id]}&times; {e.nom}
                </div>
                <div className="panier-item-prix">{fmt$(e.prix * selectionExtras[e.id])}</div>
              </div>
            ))}
          </>
        )}
      </div>
      {nbItems > 0 && (
        <div className="panier-footer">
          {nbPersonnes > 0 && (
            <div className="panier-personnes">
              Pour {nbPersonnes} personne{nbPersonnes > 1 ? 's' : ''}
            </div>
          )}
          <div className="panier-total-ligne">
            <span>Total</span>
            <span className="panier-total-montant">{fmt$(total)}</span>
          </div>
          {slotSelectionne && (
            <div className="panier-creneau">
              <Icon name="heure" size={12} />
              {' '}Créneau : {slotSelectionne}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Confettis Escamel ─────────────────────────
function lancerConfettis() {
  const duration = 3000;
  const end = Date.now() + duration;
  const colors = ['#c8941a', '#d4a017', '#f0ebe0', '#131210', '#8a8370'];
  function frame() {
    confetti({ particleCount: 3, angle: 60,  spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  }
  frame();
}

// ─── Composant principal ───────────────────────
export default function Precommande() {
  const { slug }     = useParams();
  const location     = useLocation();
  const directionRef = useRef('forward');
  const appSettings  = useSettings();
  const searchParams = new URLSearchParams(location.search);
  const isPreview    = searchParams.get('preview') === '1';
  const previewToken = searchParams.get('preview_token') || '';

  const [evenement, setEvenement]               = useState(null);
  const [chargement, setChargement]             = useState(true);
  const [erreurChargement, setErreurChargement] = useState('');

  const [etape, setEtape]           = useState(1);
  const [transition, setTransition] = useState('');

  // Étape 1 — Plateaux
  const [selection, setSelection]   = useState({});

  // Étape 2 — Extras & Allergies & Boissons
  const [selectionExtras, setSelectionExtras]   = useState({});
  const [allergies, setAllergies]               = useState([]);
  const [notesAllergies, setNotesAllergies]     = useState('');
  const [boissonsChoisies, setBoissonsChoisies] = useState({});

  // Étape 3 — Créneau
  const [slotSelectionne, setSlot] = useState('');

  // Étape 4 — Coordonnées
  const [coordonnees, setCoordonnees] = useState({
    prenom: '', nom: '', courriel: '', telephone: '', notes: '',
  });

  const [erreurEtape, setErreurEtape]  = useState('');
  const [erreursChamps, setErrsChamps] = useState({});
  const [confettisFait, setConfettisFait] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') === '1' && !confettisFait) {
      setTimeout(lancerConfettis, 400);
      setConfettisFait(true);
    }
  }, [confettisFait]);

  // Pré-sélection plateau depuis PageEvenement
  const plateauPreselectionne = location.state?.plateauPreselectionne;
  useEffect(() => {
    if (plateauPreselectionne && evenement?.plateaux) {
      setSelection(prev => ({
        ...prev,
        [plateauPreselectionne]: (prev[plateauPreselectionne] || 0) === 0
          ? 1
          : prev[plateauPreselectionne],
      }));
    }
  }, [plateauPreselectionne, evenement]);

  // ─── Chargement événement ──────────────────
  const chargerEvenement = useCallback(async () => {
    setChargement(true);
    setErreurChargement('');
    try {
      const authToken = localStorage.getItem('escamel_token');
      const res = await axios.get(`${API}/api/public/evenements/${slug}`, {
        params: isPreview ? { preview: '1', preview_token: previewToken } : {},
        headers: isPreview && authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      setEvenement(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setErreurChargement("Cet événement n'existe pas ou n'est plus disponible.");
      } else {
        const detail = err.response?.data?.error || err.message || '';
        setErreurChargement(`Erreur de chargement : ${detail || 'vérifiez la console'}`);
      }
    } finally {
      setChargement(false);
    }
  }, [isPreview, previewToken, slug]);

  useEffect(() => { chargerEvenement(); }, [chargerEvenement]);

  // ─── Validations ───────────────────────────
  function validerEtape1() {
    const total = Object.values(selection).reduce((acc, v) => acc + v, 0);
    if (total === 0) {
      setErreurEtape('Sélectionnez au moins 1 plateau pour continuer.');
      return false;
    }
    return true;
  }

  function validerEtape2() { return true; }

  async function validerEtape3() {
    if (!slotSelectionne) {
      setErreurEtape('Sélectionnez un créneau horaire pour continuer.');
      return false;
    }
    try {
      const authToken = localStorage.getItem('escamel_token');
      const res = await axios.get(`${API}/api/public/evenements/${slug}`, {
        params: isPreview ? { preview: '1', preview_token: previewToken } : {},
        headers: isPreview && authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      const evFrais   = res.data;
      setEvenement(evFrais);
      const slotFrais = (evFrais.slots || []).find(s => s.heure === slotSelectionne);
      const max       = evFrais.capacite_par_slot || 40;
      if (slotFrais && slotFrais.nb_personnes >= max) {
        setErreurEtape('Ce créneau vient de se remplir. Veuillez en choisir un autre.');
        setSlot('');
        return false;
      }
    } catch {}
    return true;
  }

  function validerEtape4() {
    const errs = {};
    if (!coordonnees.prenom?.trim()) errs.prenom  = 'Prénom requis.';
    if (!coordonnees.nom?.trim())    errs.nom     = 'Nom requis.';
    if (!coordonnees.courriel?.trim()) {
      errs.courriel = 'Courriel requis.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(coordonnees.courriel)) {
      errs.courriel = 'Format de courriel invalide.';
    }
    setErrsChamps(errs);
    return Object.keys(errs).length === 0;
  }

  // ─── Navigation avec transition ────────────
  function allerEtape(nouvelleEtape) {
    directionRef.current = nouvelleEtape > etape ? 'forward' : 'back';
    const sortie = directionRef.current === 'forward' ? 'slide-out-left' : 'slide-out-right';
    const entree = directionRef.current === 'forward' ? 'slide-in-right' : 'slide-in-left';
    setTransition(sortie);
    setTimeout(() => {
      setEtape(nouvelleEtape);
      setTransition(entree);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setTransition(''), 320);
    }, 280);
  }

  async function suivant() {
    setErreurEtape('');
    setErrsChamps({});
    if (etape === 1 && !validerEtape1()) return;
    if (etape === 2 && !validerEtape2()) return;
    if (etape === 3) {
      const ok = await validerEtape3();
      if (!ok) return;
    }
    if (etape === 4 && !validerEtape4()) return;
    allerEtape(etape + 1);
  }

  function precedent() {
    setErreurEtape('');
    setErrsChamps({});
    allerEtape(etape - 1);
  }

  function retourSlotDepuisErreur409(msg) {
    setErreurEtape(msg || 'Ce créneau est maintenant complet. Choisissez un autre horaire.');
    setSlot('');
    chargerEvenement();
    allerEtape(3);
  }

  function retourExtrasDepuisErreur409(extra_id) {
    if (extra_id) {
      setSelectionExtras(prev => ({ ...prev, [extra_id]: 0 }));
    }
    setErreurEtape("Un article de votre commande n'est plus disponible et a été retiré.");
    chargerEvenement();
    allerEtape(2);
  }

  function handleToggleAllergie(allergie) {
    setAllergies(prev =>
      prev.includes(allergie)
        ? prev.filter(a => a !== allergie)
        : [...prev, allergie]
    );
  }

  const nbPersonnes = (evenement?.plateaux || [])
    .reduce((acc, p) => acc + (selection[p.id] || 0) * (p.taille || p.nb_personnes || 1), 0);

  const boissonsOptions = (evenement?.extras || [])
    .filter(e => e.categorie === 'boisson_incluse')
    .map(e => e.nom);

  const boissonsChoisiesSerialisees = Object.entries(boissonsChoisies)
    .filter(([, nom]) => nom)
    .map(([idx, boisson]) => ({ personne: parseInt(idx), boisson }));

  const extrasSerialises = (evenement?.extras || [])
    .filter(e => e.categorie !== 'boisson_incluse' && (selectionExtras[e.id] || 0) > 0)
    .map(e => ({
      extra_id:      e.id,
      nom:           e.nom,
      qte:           selectionExtras[e.id],
      prix_unitaire: e.prix,
      sous_total:    e.prix * selectionExtras[e.id],
    }));

  // ─── États de chargement / erreur ──────────
  if (chargement) {
    return (
      <div className="precommande-chargement">
        <div className="spinner-grand" />
        <div className="muted" style={{ marginTop: 16, fontFamily: "'Lora', serif", fontStyle: 'italic' }}>
          Chargement…
        </div>
      </div>
    );
  }

  if (erreurChargement) {
    return (
      <div className="precommande-erreur-page">
        <div className="precommande-logo">ESCAMEL</div>
        <div className="erreur-msg">{erreurChargement}</div>
      </div>
    );
  }

  const fermePre = !isPreview && evenement?.date_limite_precommande
    && new Date(evenement.date_limite_precommande) < new Date();
  const nonPublie = !isPreview && evenement?.statut !== 'publie' && evenement?.statut !== 'complet';

  if (nonPublie || fermePre) {
    return (
      <div className="precommande-erreur-page">
        <div className="precommande-logo">ESCAMEL</div>
        <div className="ev-nom-header">{evenement?.nom}</div>
        <div className="erreur-msg">
          {nonPublie
            ? "Cet événement n'accepte pas encore les précommandes."
            : 'La période de précommande pour cet événement est terminée.'}
        </div>
      </div>
    );
  }

  const TOTAL_ETAPES_NAV = ETAPES.length;

  const plateauxSelectionnesIds = Object.entries(selection)
    .filter(([, qte]) => qte > 0)
    .map(([id]) => parseInt(id));

  const extrasVisibles = (evenement?.extras || [])
    .filter(e => e.categorie !== 'boisson_incluse')
    .filter(extra => {
      if (!extra.plateaux_limites) return true;
      const limites = Array.isArray(extra.plateaux_limites)
        ? extra.plateaux_limites
        : JSON.parse(extra.plateaux_limites || '[]');
      return plateauxSelectionnesIds.some(id => limites.includes(id));
    });

  return (
    <div className="precommande-page">

      {/* Bannière mode prévisualisation */}
      {isPreview && (
        <div className="precommande-preview-banner">
          <Icon name="attention" size={16} weight="fill" color="#fff" />
          Mode prévisualisation — Non visible du public
        </div>
      )}

      {/* Header fixe */}
      <header className="precommande-header">
        <div className="header-inner">
          <div className="header-logo">
            {appSettings.logo_url
              ? <img src={appSettings.logo_url} alt="Escamel" />
              : <span className="header-logo-text">ESCAMEL</span>
            }
          </div>
          <div className="header-divider" />
          <div className="header-ev-info">
            <div className="header-ev-nom">{evenement.nom}</div>
            <div className="header-ev-meta">
              {fmtDate(evenement.date_event || evenement.date_evenement)}
              {evenement.lieu ? ` · ${evenement.lieu}` : ''}
            </div>
          </div>
        </div>
      </header>

      {/* Message d'accueil dynamique */}
      {etape === 1 && evenement?.message_accueil && (
        <div className="precommande-accueil-msg">
          {evenement.message_accueil}
        </div>
      )}

      {/* Stepper */}
      {etape <= TOTAL_ETAPES_NAV && <Stepper etape={etape} />}

      {/* Layout 2 colonnes */}
      <div className="precommande-layout">
        {/* Colonne principale */}
        <main className="precommande-main">
          <div className={`etape-contenu ${transition}`}>
            {etape === 1 && (
              <EtapePlateaux
                evenement={evenement}
                selection={selection}
                onChanger={(id, val) => { setSelection(prev => ({ ...prev, [id]: val })); setErreurEtape(''); }}
                erreur={erreurEtape}
              />
            )}
            {etape === 2 && (
              <EtapeExtras
                extras={extrasVisibles}
                selectionExtras={selectionExtras}
                onChangerExtra={(id, qte) => setSelectionExtras(prev => ({ ...prev, [id]: qte }))}
                allergies={allergies}
                onToggleAllergie={handleToggleAllergie}
                notesAllergies={notesAllergies}
                onNotesAllergies={setNotesAllergies}
                nbPersonnes={nbPersonnes}
                boissonsOptions={boissonsOptions}
                boissonsChoisies={boissonsChoisies}
                onBoissonChoisie={(idx, nom) => setBoissonsChoisies(prev => ({ ...prev, [idx]: nom }))}
                erreur={erreurEtape}
              />
            )}
            {etape === 3 && (
              <EtapeSlot
                evenement={evenement}
                slotSelectionne={slotSelectionne}
                onSelectionner={h => { setSlot(h); setErreurEtape(''); }}
                erreur={erreurEtape}
              />
            )}
            {etape === 4 && (
              <EtapeCoordonnees
                coordonnees={coordonnees}
                onChange={c => { setCoordonnees(c); setErrsChamps({}); }}
                erreurs={erreursChamps}
              />
            )}
            {etape === 5 && (
              <EtapeConfirmation
                evenement={evenement}
                selection={selection}
                slotSelectionne={slotSelectionne}
                coordonnees={coordonnees}
                extras={extrasSerialises}
                allergies={allergies}
                notesAllergies={notesAllergies}
                boissonsChoisies={boissonsChoisiesSerialisees}
                isPreview={isPreview}
                previewToken={previewToken}
                onRetourSlot={retourSlotDepuisErreur409}
                onRetourExtras={retourExtrasDepuisErreur409}
              />
            )}
          </div>

          {/* Navigation */}
          {etape < 5 && (
            <div className="nav-etapes">
              {etape > 1 && (
                <button className="btn btn-ghost" onClick={precedent}>
                  <Icon name="retour" size={15} weight="bold" />
                  Retour
                </button>
              )}
              <button className="btn btn-primary nav-suivant" onClick={suivant}>
                {etape === 4 ? 'Vérifier ma commande' : 'Continuer'}
                {etape < 4 && <Icon name="chevron" size={16} weight="bold" />}
              </button>
            </div>
          )}
        </main>

        {/* Panier flottant (desktop) */}
        {etape < 5 && (
          <aside className="panier-sidebar">
            <PanierFlottant
              evenement={evenement}
              selection={selection}
              selectionExtras={selectionExtras}
              slotSelectionne={slotSelectionne}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
